import React from "react";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import Testimonials from "@/components/Testimonials";
import Seo from "@/components/Seo";
import {
  Coffee,
  Award,
  GraduationCap,
  Users,
  Briefcase,
  Sparkles,
  Building2,
  Star,
  CheckCircle2,
  Instagram,
  Mail,
  MessageCircle,
  FileText,
  ExternalLink,
} from "lucide-react";

// Map icon string (stored in DB) to lucide component
const ICONS = { Coffee, Award, GraduationCap, Users, Briefcase, Sparkles, Building2, Star };

const PHOTO_HERO = "https://customer-assets.emergentagent.com/job_brew-master-34/artifacts/w2zz7s6m_IMG_9982.jpeg";
const PHOTO_BARISTA = "https://customer-assets.emergentagent.com/job_brew-master-34/artifacts/7fd5vztw_IMG_9975.jpeg";
const PHOTO_TEACHING = "https://customer-assets.emergentagent.com/job_brew-master-34/artifacts/5f8tet6f_IMG_9977.jpeg";
const PHOTO_CELEBRATION = "https://customer-assets.emergentagent.com/job_brew-master-34/artifacts/0hsefnqt_IMG_9974.jpeg";
const PHOTO_LATTE = "https://customer-assets.emergentagent.com/job_brew-master-34/artifacts/rv0spadh_IMG_9979.jpeg";
const PHOTO_ESPRESSO_CLOSEUP = "https://customer-assets.emergentagent.com/job_brew-master-34/artifacts/vwaje9cr_IMG_9978.jpeg";
const PHOTO_CERTS_WALL = "https://customer-assets.emergentagent.com/job_brew-master-34/artifacts/33148xdj_IMG_9976.jpeg";
const PHOTO_Q_GRADER = "https://customer-assets.emergentagent.com/job_brew-master-34/artifacts/jv7dk9cv_IMG_9981.jpeg";

const MOMENTS = [
  { src: PHOTO_BARISTA, ar: "خلف بار الإسبريسو", en: "Behind the espresso bar" },
  { src: PHOTO_TEACHING, ar: "تدريب — Barista Skills Professional", en: "Training — Barista Skills Professional" },
  { src: PHOTO_ESPRESSO_CLOSEUP, ar: "تحضير الإسبريسو", en: "Pulling an espresso" },
  { src: PHOTO_LATTE, ar: "صبّ اللاتيه", en: "Latte pour" },
  { src: PHOTO_CELEBRATION, ar: "بعد اجتياز Professional", en: "Right after passing Professional" },
  { src: PHOTO_CERTS_WALL, ar: "حائط الشهادات", en: "The certifications wall" },
];

const TIMELINE = [
  {
    year: "2018",
    icon: Coffee,
    ar: {
      title: "البداية — كاشير في مقهى",
      desc: "أول خطوة في عالم القهوة، حيث بدأت رحلتي خلف بار الكاشير. لم أكن أتصور أن هذه البداية البسيطة ستفتح لي عالماً كاملاً.",
    },
    en: {
      title: "The Beginning — Cashier at a Café",
      desc: "My first step into the coffee world, behind the cashier counter. I had no idea this simple start would open a whole universe.",
    },
  },
  {
    year: "2019—2021",
    icon: Briefcase,
    ar: {
      title: "باريستا — اشتعل الشغف",
      desc: "انتقلت إلى الباريستا. هنا اكتشفت أن القهوة ليست مجرد مشروب، بل علم وفن وعلاقة حميمة بين البذرة والفنجان.",
    },
    en: {
      title: "Barista — Passion Ignited",
      desc: "Moved behind the bar as a barista. That's when I realized coffee isn't just a drink — it's a science, an art, and an intimate dialogue between seed and cup.",
    },
  },
  {
    year: "2022",
    icon: GraduationCap,
    ar: {
      title: "أول دورة احترافية — SCA Barista Skills Foundation",
      desc: "أول شهادة معتمدة من الجمعية العالمية للقهوة المختصة. لحظة فاصلة — تحوّلت من شغوف بالقهوة إلى دارس لها.",
    },
    en: {
      title: "First Pro Course — SCA Barista Skills Foundation",
      desc: "My first certification from the Specialty Coffee Association. A turning point — from coffee enthusiast to coffee scholar.",
    },
  },
  {
    year: "2022—2023",
    icon: Users,
    ar: {
      title: "مشرف شفت → مدير فرع",
      desc: "ترقّيت إلى مشرف شفت ثم مدير فرع. تعلّمت أن قيادة فريق الباريستا تتطلب نفس الحب الذي نقدّمه في كل فنجان.",
    },
    en: {
      title: "Shift Supervisor → Branch Manager",
      desc: "Promoted to shift supervisor, then branch manager. I learned that leading a barista team requires the same love we pour into every cup.",
    },
  },
  {
    year: "2023",
    icon: Award,
    ar: {
      title: "إكمال مهارات الباريستا — كل المستويات",
      desc: "أنهيت Barista Skills بكامل المستويات: التأسيسي، المتوسط، والاحترافي. كل مستوى فتح طبقة جديدة من الفهم.",
    },
    en: {
      title: "Completed Barista Skills — All Levels",
      desc: "Finished all Barista Skills levels: Foundation, Intermediate, and Professional. Each level revealed a deeper layer of understanding.",
    },
  },
  {
    year: "2023",
    icon: Sparkles,
    ar: {
      title: "مهارات التقطير — Foundation & Intermediate",
      desc: "تعمّقت في علم التقطير (Brewing Skills). كل طريقة تحضير لها لغتها الخاصة، وكل وصفة قصة.",
    },
    en: {
      title: "Brewing Skills — Foundation & Intermediate",
      desc: "Dove deep into Brewing Skills. Every method speaks its own language, and every recipe tells a story.",
    },
  },
  {
    year: "2025",
    icon: Star,
    ar: {
      title: "CVA + المهارات الحسّية بكامل المستويات",
      desc: "اعتُمدت في Coffee Value Assessment ومهارات Sensory Skills بكل مستوياتها. تدرّبت على لغة النكهات، التمييز، والتقييم.",
    },
    en: {
      title: "CVA + Sensory Skills (All Levels)",
      desc: "Certified in Coffee Value Assessment and Sensory Skills across all levels. Trained the palate to discern, evaluate, and articulate flavor.",
    },
  },
  {
    year: "2025",
    icon: Award,
    ar: {
      title: "Q Grader — مُقيّم قهوة معتمد",
      desc: "حصلت على شهادة Q Grader العالمية. مرحلة نخبوية يستطيع فيها المُقيّم تحديد جودة القهوة بمعايير علمية دقيقة.",
    },
    en: {
      title: "Q Grader — Certified Coffee Evaluator",
      desc: "Earned the international Q Grader certification. An elite tier where evaluators grade coffee by rigorous scientific standards.",
    },
  },
  {
    year: "2026",
    icon: GraduationCap,
    ar: {
      title: "AST — مدرّب معتمد من SCA",
      desc: "أصبحت Authorized SCA Trainer — مدرّب معتمد من الجمعية العالمية للقهوة المختصة. الآن أعلّم ما أحببته.",
    },
    en: {
      title: "AST — Authorized SCA Trainer",
      desc: "Became an Authorized SCA Trainer. Now I teach what I love, certified by the global Specialty Coffee Association.",
    },
  },
  {
    year: "الآن · Today",
    icon: Building2,
    ar: {
      title: "مدرّب ومدير جودة — محمصة الرياض",
      desc: "أعمل حالياً مدرّباً ومديراً للجودة في محمصة الرياض. كل يوم فرصة لرفع معايير القهوة في المملكة، ولنقل الشغف للجيل القادم.",
    },
    en: {
      title: "Trainer & Quality Manager — Roastery of Riyadh",
      desc: "Currently serving as Trainer & Quality Manager at Roastery of Riyadh. Every day is a chance to raise coffee standards in the Kingdom and pass the passion forward.",
    },
  },
];

const CERTS = [
  { ar: "Barista Skills — Foundation / Intermediate / Professional", en: "Barista Skills — Foundation · Intermediate · Professional" },
  { ar: "Brewing Skills — Foundation / Intermediate", en: "Brewing Skills — Foundation · Intermediate" },
  { ar: "Sensory Skills — كل المستويات", en: "Sensory Skills — All Levels" },
  { ar: "Coffee Value Assessment (CVA)", en: "Coffee Value Assessment (CVA)" },
  { ar: "Q Grader Certified", en: "Q Grader Certified" },
  { ar: "AST — Authorized SCA Trainer", en: "AST — Authorized SCA Trainer" },
];

export default function About() {
  const { lang, isRTL } = useI18n();
  const ar = lang === "ar";
  const { data: about } = useQuery({ queryKey: ["about"], queryFn: () => endpoints.about() });

  // Use CMS data when loaded, fallback to static constants for first paint
  const hero = about?.hero;
  const heroPhoto = hero?.photo || PHOTO_HERO;
  const heroSecondary = hero?.photo_secondary || PHOTO_BARISTA;
  const timeline = about?.timeline?.length ? about.timeline : null;
  const moments = about?.moments?.length ? about.moments : MOMENTS;
  const certs = about?.certs?.length ? about.certs : null;
  const certsPhoto = about?.certs_photo || PHOTO_Q_GRADER;

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="about-page">
      <Seo
        title={isRTL ? "عن عبدالله الكاساني — مدرّب القهوة المختصة" : "About Abdullah Cassani — Specialty Coffee Educator"}
        description={isRTL
          ? "عبدالله الكاساني — مدرّب قهوة مختصة معتمد من SCA، Q-Grader، مؤسس أكاديمية علم القهوة. سنوات من التدريب، شهادات دولية، وشغف بنقل علم القهوة للعالم العربي."
          : "Abdullah Cassani — SCA-certified specialty coffee educator and Q-Grader, founder of Coffee Science Academy. Bringing global coffee science to the Arab world."}
        keywords="عبدالله الكاساني, Abdullah Cassani, مدرّب قهوة, Q-Grader, مدرّب SCA, مؤسس أكاديمية القهوة"
        path="/about"
        jsonLdId="about-jsonld"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "عبدالله الكاساني",
          "alternateName": "Abdullah Cassani",
          "url": "https://abdullah-coffee-science.academy/about",
          "image": "https://abdullah-coffee-science.academy/og-image.jpg",
          "jobTitle": "Specialty Coffee Educator & SCA-Certified Q-Grader",
          "worksFor": { "@type": "EducationalOrganization", "name": "Abdullah Coffee Science Academy" }
        }}
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#2A2421] text-[#F9F6F0]">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(ellipse 80% 50% at 100% 0%, #C86240 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 0% 100%, #6B4F3A 0%, transparent 70%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C86240]/15 border border-[#C86240]/30 text-[#E89A7C] text-xs uppercase tracking-widest mb-6">
              <Coffee size={14} />
              {ar ? "قصّتي مع القهوة" : "My Coffee Story"}
            </div>
            <h1
              className={`${
                ar ? "font-serif-ar" : "font-display"
              } text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight mb-6`}
            >
              {hero ? (ar ? hero.title_ar : hero.title_en) : (ar ? "مَن عبدالله الكاساني؟" : "Who is Abdullah Cassani?")}
            </h1>
            <p className="text-lg sm:text-xl text-[#E0D7C8] leading-relaxed">
              {hero ? (ar ? hero.subtitle_ar : hero.subtitle_en) : (ar
                ? "رحلة شغف بدأت خلف بار كاشير عام 2018، وامتدّت لتصبح علماً، دورات، شهادات عالمية، وقيادة جودة في إحدى أعرق محمصات الرياض. هذي قصّتي مع القهوة."
                : "A passion that began behind a cashier counter in 2018 — and grew into science, certifications, and leadership at one of Riyadh's premier roasteries. This is my coffee story.")}
            </p>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <img
                src={heroPhoto}
                alt="Abdullah Cassani"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 -start-5 hidden md:block w-32 aspect-square rounded-xl overflow-hidden border border-white/10 shadow-xl">
              <img src={heroSecondary} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <h2
          className={`${
            ar ? "font-serif-ar" : "font-display"
          } text-2xl sm:text-3xl text-[#2A2421] mb-10 flex items-center gap-3`}
        >
          <span className="w-10 h-px bg-[#C86240]" />
          {ar ? "الرحلة" : "The Journey"}
        </h2>

        <div className="relative">
          {/* Vertical line */}
          <div
            className={`absolute top-0 bottom-0 ${
              isRTL ? "right-5 sm:right-7" : "left-5 sm:left-7"
            } w-px bg-gradient-to-b from-[#C86240] via-[#2A2421]/20 to-transparent`}
            aria-hidden
          />

          <ol className="space-y-8">
            {(timeline || TIMELINE.map((s, i) => ({ ...s, id: `tl-${i}`, icon: s.icon?.displayName }))).map((step, idx) => {
              const Icon = timeline
                ? (ICONS[step.icon] || Coffee)
                : (TIMELINE[idx]?.icon || Coffee);
              const title = timeline ? (ar ? step.title_ar : step.title_en) : (ar ? step.ar.title : step.en.title);
              const desc = timeline ? (ar ? step.desc_ar : step.desc_en) : (ar ? step.ar.desc : step.en.desc);
              return (
                <li
                  key={step.id || `${step.year}-${idx}`}
                  className={`relative ${isRTL ? "pr-14 sm:pr-20" : "pl-14 sm:pl-20"}`}
                  data-testid={`about-step-${idx}`}
                >
                  {/* Icon node */}
                  <div
                    className={`absolute top-0 ${
                      isRTL ? "right-0" : "left-0"
                    } w-10 h-10 sm:w-14 sm:h-14 rounded-2xl bg-white border border-[#2A2421]/10 shadow-sm flex items-center justify-center text-[#C86240] z-[1]`}
                  >
                    <Icon size={22} />
                  </div>

                  <div className="rounded-2xl bg-white border border-[#2A2421]/8 p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="text-xs uppercase tracking-widest text-[#C86240] font-semibold mb-1.5">
                      {step.year}
                    </div>
                    <h3
                      className={`${
                        ar ? "font-serif-ar" : "font-display"
                      } text-lg sm:text-xl text-[#2A2421] mb-2 leading-snug`}
                    >
                      {title}
                    </h3>
                    <p className="text-sm sm:text-base text-[#5C544F] leading-relaxed">
                      {desc}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Moments — photo gallery */}
      <section className="bg-white border-t border-[#2A2421]/8">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <h2
            className={`${
              ar ? "font-serif-ar" : "font-display"
            } text-2xl sm:text-3xl text-[#2A2421] mb-2 flex items-center gap-3`}
          >
            <span className="w-10 h-px bg-[#C86240]" />
            {ar ? "لحظات من الرحلة" : "Moments from the Journey"}
          </h2>
          <p className="text-sm text-[#5C544F] mb-8 max-w-2xl">
            {ar
              ? "صور من بار الإسبريسو، قاعات التدريب، ومحطات التتويج خلال السنوات الماضية."
              : "Snapshots from the espresso bar, training rooms, and milestones along the way."}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            {moments.map((m, i) => {
              const isApi = !!m.id;
              const src = isApi ? m.src : m.src;
              const caption = isApi ? (ar ? m.caption_ar : m.caption_en) : (ar ? m.ar : m.en);
              return (
                <figure
                  key={m.id || i}
                  className="group relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#2A2421]/8 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
                  data-testid={`moment-${i}`}
                >
                  <img
                    src={src}
                    alt={caption}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <figcaption className="absolute bottom-3 start-3 end-3 text-white text-xs sm:text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    {caption}
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications grid */}
      <section className="bg-[#F1EBE2] border-y border-[#2A2421]/8">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
          <h2
            className={`${
              ar ? "font-serif-ar" : "font-display"
            } text-2xl sm:text-3xl text-[#2A2421] mb-2 flex items-center gap-3`}
          >
            <span className="w-10 h-px bg-[#C86240]" />
            {ar ? "الشهادات المعتمدة" : "Certifications"}
          </h2>
          <p className="text-sm text-[#5C544F] mb-8">
            {ar
              ? "كل الشهادات معتمدة من الجمعية العالمية للقهوة المختصة (SCA)"
              : "All certifications by the Specialty Coffee Association (SCA)"}
          </p>
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#2A2421]/10 shadow-md">
                <img
                  src={certsPhoto}
                  alt={ar ? "تسلّم شهادة Q Grader" : "Receiving the Q Grader license"}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                  <div className="text-white text-xs sm:text-sm font-medium">
                    {ar ? "تسلّم شهادة Licensed Q Grader" : "Receiving the Licensed Q Grader license"}
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
              {(certs || CERTS.map((c, i) => ({ id: `c-${i}`, title_ar: c.ar, title_en: c.en, pdf: null }))).map((c, i) => {
                const title = ar ? (c.title_ar || c.ar) : (c.title_en || c.en);
                const Tag = c.pdf ? "a" : "div";
                const props = c.pdf
                  ? { href: c.pdf, target: "_blank", rel: "noopener noreferrer" }
                  : {};
                return (
                  <Tag
                    key={c.id || i}
                    {...props}
                    className={`flex items-start gap-3 rounded-xl bg-white border border-[#2A2421]/8 px-4 py-3.5 ${c.pdf ? "hover:border-[#C86240]/40 hover:shadow-md transition-all cursor-pointer" : ""}`}
                    data-testid={`about-cert-${i}`}
                  >
                    <CheckCircle2 className="shrink-0 text-[#C86240] mt-0.5" size={18} />
                    <span className="text-sm sm:text-base text-[#2A2421] font-medium leading-snug flex-1">
                      {title}
                    </span>
                    {c.pdf && (
                      <span className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#C86240] font-semibold">
                        <FileText size={12} /> PDF <ExternalLink size={10} />
                      </span>
                    )}
                  </Tag>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Connect CTA */}
      <Testimonials />
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="rounded-3xl bg-[#2A2421] text-[#F9F6F0] p-8 sm:p-12 text-center">
          <h2
            className={`${
              ar ? "font-serif-ar" : "font-display"
            } text-2xl sm:text-3xl mb-3`}
          >
            {ar ? "تواصل معي" : "Let's Connect"}
          </h2>
          <p className="text-[#E0D7C8] text-sm sm:text-base mb-8 max-w-2xl mx-auto">
            {ar
              ? "للدورات، الورش، التواصل المباشر، أو حتى مجرد محادثة حول فنجان قهوة."
              : "For courses, workshops, direct inquiries, or simply a chat over a cup of coffee."}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="https://www.instagram.com/abdullahcassani?igsh=cjNoc212Znd0OTM0&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#F9F6F0] text-[#2A2421] text-sm font-semibold hover:bg-white transition-colors"
              data-testid="about-instagram"
            >
              <Instagram size={18} />
              @abdullahcassani
            </a>
            <a
              href="https://wa.me/966567028296"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
              data-testid="about-whatsapp"
            >
              <MessageCircle size={18} />
              {ar ? "واتساب" : "WhatsApp"}
            </a>
            <a
              href="mailto:Abdullahalcassani@gmail.com"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#C86240] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
              data-testid="about-email"
            >
              <Mail size={18} />
              {ar ? "البريد" : "Email"}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
